import React from "react";
import {
  Box,
  Typography,
  Tooltip,
  IconButton,
  Collapse,
} from "@material-ui/core";
import {
  FolderOpen,
  InsertDriveFile,
  CheckCircle,
  RadioButtonChecked,
  RadioButtonUnchecked,
  ChevronRight,
  ExpandMore,
  Close,
  Undo,
  History,
} from "@material-ui/icons";
import FileCopyOutlinedIcon from "@material-ui/icons/FileCopyOutlined";
import {formatDate} from "common/utils/format_date";
import UploadStatus from "routes/matter/matter_editor/components/upload_status";
import isMatterDocumentComplete from "routes/matter/matter_editor/utils/is_matter_document_uploading";

import {
  MatterTreeNode as TreeNodeType,
  MatterDocument,
  Collection,
} from "common/types/matter";

const styles = {
  nodeContainer: {
    userSelect: "none",
    position: "relative",
  },
  nodeContent: {
    display: "flex",
    alignItems: "center",
    padding: "6px 10px",
    borderRadius: "4px",
  },
  selectableNode: {
    cursor: "pointer",
  },
  selectableNodeHover: {
    backgroundColor: "#f5f5f5",
  },
  selectedNode: {
    backgroundColor: "#1976d220",
    color: "#1976d2",
  },
  uploadTargetNode: {
    backgroundColor: "#e3f2fd",
    boxShadow: "inset 0 0 0 2px #1976d2",
  },
  pendingNode: {
    boxShadow: "inset 0 0 0 2px #1976d2",
  },
  icon: {
    marginRight: "10px",
    fontSize: "1.4rem",
  },
  toggleButton: {
    padding: "2px",
    marginRight: "2px",
  },
  togglePlaceholder: {
    width: "24px",
    marginRight: "2px",
    flexShrink: 0,
  },
  toggleIcon: {
    fontSize: "1.2rem",
    color: "#666",
  },
  collectionIcon: {
    color: "#1976d2",
  },
  documentIcon: {
    color: "#666",
  },
  nodeText: {
    flexGrow: 1,
    overflowWrap: "anywhere",
  },
  collectionText: {
    fontWeight: 500,
  },
  documentTextProcessing: {
    color: "#666",
    opacity: 0.9,
  },
  documentTextComplete: {
    fontWeight: 400,
  },
  documentMeta: {
    fontSize: "0.75rem",
    color: "#666",
    marginTop: "2px",
    display: "flex",
    flexWrap: "wrap",
    gap: "4px",
  },
  metaSpan: {
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  children: {
    marginLeft: "10px",
    paddingLeft: "10px",
    borderLeft: "1px solid #ececec",
  },
  documentActions: {
    marginLeft: "8px",
    flexShrink: 0,
    alignSelf: "flex-start",
    height: "24px",
    display: "flex",
    alignItems: "center",
    gap: "8px",
    zIndex: 10,
  },
  uploadStatus: {
    width: "100%",
    maxWidth: "16em",
  },
  checklistButtonContainer: {
    position: "absolute",
    right: "8px",
    top: "50%",
    transform: "translateY(-50%)",
    zIndex: 10,
  },
  checklistButton: {
    padding: "4px",
    borderRadius: "50%",
  },
  checklistButtonHidden: {
    opacity: 0,
  },
  checklistButtonVisible: {
    opacity: 1,
  },
  checklistButtonActive: {
    backgroundColor: "#e8f5e9",
    cursor: "default",
  },
  checklistButtonInactive: {
    cursor: "pointer",
  },
  checklistIcon: {
    fontSize: "1.3rem",
  },
  checklistIconActive: {
    color: "#4caf50",
    fontWeight: "bold",
  },
  checklistIconInactive: {
    color: "#999",
    fontWeight: "normal",
  },
  nameRow: {
    display: "flex",
    alignItems: "center",
    gap: "4px",
  },
  staleIcon: {
    fontSize: "1.3rem",
    color: "#f9a825",
  },
  addButton: {
    padding: "4px",
    borderRadius: "50%",
  },
  addButtonActive: {
    backgroundColor: "#e3f2fd",
  },
  addIcon: {
    fontSize: "1.3rem",
    color: "#1976d2",
  },
  pendingText: {
    color: "#9e9e9e",
    fontStyle: "italic",
    // Italic glyphs overhang their box; without a little right padding the
    // trailing character's slant gets clipped by nodeText's `overflow: hidden`.
    paddingRight: "2px",
  },
  expandSiblingsRow: {
    display: "flex",
    alignItems: "center",
    gap: "4px",
    padding: "4px 10px",
    borderRadius: "4px",
    cursor: "pointer",
    color: "#1976d2",
    fontSize: "0.8rem",
    fontStyle: "italic",
    userSelect: "none",
  },
} as const;

// Contract-type id for SOW documents / SOW contract-type folders.
const MSA_TYPE = 1;
const MSOW_TYPE = 2;
const SOW_TYPE = 3;

const getUploaderName = (doc: MatterDocument): string => {
  return doc.first_name || doc.last_name
    ? `${doc.first_name || ""} ${doc.last_name || ""}`.trim()
    : doc.username;
};

interface MatterTreeNodeProps {
  node: TreeNodeType;
  level: number;
  onNodeSelect: (node: TreeNodeType) => void;
  selectedNodeId?: number;
  hideDocumentMeta?: boolean;
  // True for the individual file documents nested directly under a combined
  // document node. Their upload info is shown on the combined node instead, so
  // it is suppressed here.
  insideCombinedDocument?: boolean;
  // True when the enclosing combined document node is itself pending (a wholly new
  // combined upload). When it is NOT (a mixed combined doc — existing files plus a
  // new one), a pending file shows its own "To be uploaded" label instead.
  combinedDocumentPending?: boolean;
  displayInDocDetail?: boolean;
  checklistDocumentId?: number;
  onChecklistDocumentSelect?: (documentId: number) => void;
  isViewingMSA?: boolean;
  secondaryDocumentLoading?: boolean;
  onAddPursuantUpload?: (node: TreeNodeType) => void;
  canUploadMsow?: boolean;
  uploadTargetNodeId?: number;
  // True while the user is staging a targeted upload. When set, clicking an
  // addable document row selects it as the upload target instead of opening it.
  isAddingFiles?: boolean;
  // Ids of collection nodes that should start collapsed (used to keep an upload
  // preview focused on the relevant branch).
  defaultCollapsedIds?: Set<number>;
  // Changes when the upload target changes; nodes re-apply `defaultCollapsedIds`
  // when it does, so retargeting refocuses the tree (animated, not remounted).
  collapseKey?: number | string;
  // Called when this node expands/collapses or reveals its hidden siblings, so
  // the view can scroll the newly revealed content into view once it settles.
  onRequestScroll?: (element: HTMLElement | null) => void;
  // Remove/restore an existing MSA file while staging a new one (to "replace").
  onRemoveMsaFile?: (node: TreeNodeType) => void;
  // Ids of existing MSA documents the user has marked for removal.
  removedMsaIds?: Set<number>;
  // False when only one MSA would remain — the last one can't be removed.
  canRemoveMsa?: boolean;
  // creation_date of the matter's newest MSA. A SOW created before it was analysed
  // against an older MSA set (old SOWs aren't re-analysed) → shown as stale.
  newestMsaCreationDate?: string;
}

export const MatterTreeNode: React.FC<MatterTreeNodeProps> = ({
  node,
  level,
  onNodeSelect,
  selectedNodeId,
  hideDocumentMeta = false,
  insideCombinedDocument = false,
  combinedDocumentPending = false,
  displayInDocDetail = false,
  checklistDocumentId,
  onChecklistDocumentSelect,
  isViewingMSA = false,
  secondaryDocumentLoading = false,
  onAddPursuantUpload,
  canUploadMsow = false,
  uploadTargetNodeId,
  isAddingFiles = false,
  defaultCollapsedIds,
  collapseKey,
  onRequestScroll,
  onRemoveMsaFile,
  removedMsaIds,
  canRemoveMsa = true,
  newestMsaCreationDate,
}) => {
  const [isHovered, setIsHovered] = React.useState(false);
  const [isExpanded, setIsExpanded] = React.useState(
    () => !(defaultCollapsedIds?.has(node.id) ?? false),
  );
  // Whether the existing (non-pending) sibling SOWs collapsed behind a summary
  // line are currently revealed. Reset when the upload target changes.
  const [showOtherSiblings, setShowOtherSiblings] = React.useState(false);
  // Marks the bottom of this node's subtree; scrolled into view after an
  // expand/reveal so the newly shown content is brought on-screen.
  const endMarkerRef = React.useRef<HTMLDivElement>(null);

  // Re-apply the default collapse state when the upload target changes
  // (collapseKey), so retargeting via a "+" animates the tree to the new focus.
  // A target change recomputes defaultCollapsedIds in the same render, so we
  // read the latest here. Manual toggles persist within a target because the
  // key is otherwise stable.
  const prevCollapseKeyRef = React.useRef(collapseKey);
  React.useEffect(() => {
    if (prevCollapseKeyRef.current !== collapseKey) {
      prevCollapseKeyRef.current = collapseKey;
      setIsExpanded(!(defaultCollapsedIds?.has(node.id) ?? false));
      setShowOtherSiblings(false);
    }
  }, [collapseKey, defaultCollapsedIds, node.id]);

  const hasChildren = node.children.length > 0;
  const isSelected = selectedNodeId === node.id;
  const isCollection = node.type === "collection";
  const isDocument = node.type === "document";
  const isDocumentComplete =
    isDocument && isMatterDocumentComplete(node.data as MatterDocument);
  const documentTypeId = isDocument
    ? (node.data as MatterDocument).document_type_id
    : undefined;
  const isSOW = isDocument && documentTypeId === SOW_TYPE;
  // A SOW or MSOW analysed against an older MSA than the current one. Old
  // documents are not re-analysed when a new MSA file is added, so anything
  // created before the newest MSA was reviewed against a previous MSA set — and
  // an MSOW's stored analysis also keeps serving that older view to new SOWs
  // beneath it. (Same-batch uploads share a creation_date — one transaction —
  // so `<` never flags them.)
  const isStaleDocument =
    isDocument &&
    (documentTypeId === SOW_TYPE || documentTypeId === MSOW_TYPE) &&
    !node.pending &&
    newestMsaCreationDate !== undefined &&
    new Date((node.data as MatterDocument).creation_date) <
      new Date(newestMsaCreationDate);
  const isChecklistDocument = checklistDocumentId === node.id;
  const isCombinedDocument =
    isCollection &&
    (node.data as Collection).collection_type === "CombinedDocument";
  // The combined MSA group carries the "+" control; it targets a member file
  // under the hood (every member resolves to the same revision).
  const combinedMsaTargetChild =
    isCombinedDocument &&
    !node.pending &&
    (node.data as Collection).dc_document_type === MSA_TYPE
      ? node.children.find(child => child.type === "document" && !child.pending)
      : undefined;
  // The "+" action applies to real MSA/MSOW documents or the combined MSA group
  // as a whole - never to an individual file inside a combined document.
  const canAddPursuantUpload =
    Boolean(onAddPursuantUpload) &&
    !node.pending &&
    !insideCombinedDocument &&
    ((isDocument &&
      (documentTypeId === MSA_TYPE || documentTypeId === MSOW_TYPE)) ||
      combinedMsaTargetChild !== undefined);
  // An existing MSA file shown inside the combined-MSA preview while staging a new
  // MSA can be removed to "replace" the old one. Its own row carries the control.
  const isRemovableMsaFile =
    Boolean(onRemoveMsaFile) &&
    isDocument &&
    !node.pending &&
    documentTypeId === MSA_TYPE &&
    insideCombinedDocument &&
    isAddingFiles;
  const isMsaRemoved = isDocument && Boolean(removedMsaIds?.has(node.id));
  // The target highlight shows on the row carrying the "+" control; member rows
  // inside a combined group never highlight.
  const isUploadTarget =
    uploadTargetNodeId !== undefined &&
    !insideCombinedDocument &&
    (uploadTargetNodeId === node.id ||
      uploadTargetNodeId === combinedMsaTargetChild?.id);
  // While staging an upload, clicking an addable document row targets it (instead
  // of opening it), so the whole row acts as the target selector.
  const canSelectAsUploadTarget = canAddPursuantUpload && isAddingFiles;
  const showUploadStatus = isDocument && !hideDocumentMeta && !node.pending;
  // An MSA parent can also take MSOWs when the user is permitted; everything
  // else (e.g. an MSOW parent) only accepts SOWs.
  const addPursuantUploadTooltip =
    documentTypeId === MSA_TYPE && canUploadMsow
      ? "Add more SOW/MSOW documents under this file"
      : "Add more SOW Documents under this file";

  const handleSelect = () => {
    if (isSelected) {
      return;
    }
    onNodeSelect(node);
  };

  const handleChecklistSelect = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (isChecklistDocument) {
      return;
    }
    if (isSOW && onChecklistDocumentSelect && node.id) {
      onChecklistDocumentSelect(node.id);
    }
  };

  const handleToggleExpand = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsExpanded(prev => !prev);
    // Reveal the resulting content (the expanded children, or the row once its
    // children are gone) after the collapse animation settles.
    onRequestScroll?.(endMarkerRef.current);
  };

  const handleAddPursuantUpload = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (canAddPursuantUpload && onAddPursuantUpload) {
      onAddPursuantUpload(combinedMsaTargetChild ?? node);
    }
  };

  const handleRemoveMsaFile = (e: React.MouseEvent) => {
    // Render/disabled conditions already guard this handler.
    e.stopPropagation();
    onRemoveMsaFile?.(node);
  };

  const isSelectable = node.type === "document" && !isSelected && !node.pending;

  // Clicking the row either targets it for upload (while adding files) or falls
  // back to the existing open-document behaviour.
  const handleRowClick = () => {
    if (canSelectAsUploadTarget) {
      if (!isUploadTarget && onAddPursuantUpload) {
        onAddPursuantUpload(combinedMsaTargetChild ?? node);
      }
      return;
    }
    if (isSelectable) {
      handleSelect();
    }
  };
  const isRowClickable = canSelectAsUploadTarget || isSelectable;

  // A SOW contract-type folder (not a CombinedDocument). When a pending upload
  // has been merged into such a folder, its existing SOWs are collapsed behind a
  // summary so the in-progress upload stays in focus.
  const isSowFolder =
    isCollection &&
    !isCombinedDocument &&
    (node.data as Collection).dc_document_type === SOW_TYPE;

  // Existing SOWs collapse behind a summary while a pending upload is merged into
  // this folder, keeping the in-progress upload in focus. `injectPendingUpload`
  // only mixes pending nodes into the target's folder, so this scopes itself.
  const otherSiblings = node.children.filter(child => !child.pending);
  const hasPendingChild = node.children.some(child => child.pending);
  const collapseOtherSiblings =
    isSowFolder && hasPendingChild && otherSiblings.length > 0;
  const visibleChildren =
    collapseOtherSiblings && !showOtherSiblings
      ? node.children.filter(child => child.pending)
      : node.children;

  const renderUploaderMeta = (doc: MatterDocument) => (
    <Typography variant="caption" style={styles.documentMeta}>
      <span style={styles.metaSpan}>{getUploaderName(doc)}</span>
      <span>-</span>
      <span style={styles.metaSpan}>
        {formatDate(new Date(doc.creation_date))}
      </span>
    </Typography>
  );

  const getIcon = () => {
    if (isCombinedDocument) {
      return (
        <FileCopyOutlinedIcon
          style={{...styles.icon, ...styles.collectionIcon}}
        />
      );
    }
    if (isCollection) {
      return <FolderOpen style={{...styles.icon, ...styles.collectionIcon}} />;
    }
    return <InsertDriveFile style={{...styles.icon, ...styles.documentIcon}} />;
  };

  const getNodeContentStyle = () => {
    let nodeStyle = {...styles.nodeContent};

    if (isSelected) {
      nodeStyle = {...nodeStyle, ...styles.selectedNode};
    }

    if (isUploadTarget) {
      nodeStyle = {...nodeStyle, ...styles.uploadTargetNode};
    }

    // A pending combined document draws a single border around the whole group;
    // its pending files skip their own border so the boxes merge into one.
    if (
      node.pending &&
      (isCombinedDocument || (isDocument && !insideCombinedDocument))
    ) {
      nodeStyle = {...nodeStyle, ...styles.pendingNode};
    }

    if (isRowClickable) {
      nodeStyle = {...nodeStyle, ...styles.selectableNode};
      // The upload-target highlight is a fixed cue; don't let hover override it.
      if (isHovered && !isUploadTarget) {
        nodeStyle = {...nodeStyle, ...styles.selectableNodeHover};
      }
    }

    return {
      ...nodeStyle,
      paddingLeft: level * 4 + 8,
      cursor: isRowClickable ? "pointer" : "default",
    };
  };

  return (
    <div
      style={styles.nodeContainer}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      // Marks a pending upload preview row so the tree scrolls here (rather than
      // its very bottom) when the upload is targeted. See MatterTreeView.
      data-scroll-anchor={node.pending ? "true" : undefined}
    >
      <Box
        style={getNodeContentStyle()}
        onClick={isRowClickable ? handleRowClick : undefined}
      >
        {hasChildren ? (
          <IconButton
            size="small"
            onClick={handleToggleExpand}
            style={styles.toggleButton}
            disableRipple={true}
            aria-label={isExpanded ? "Collapse" : "Expand"}
          >
            {isExpanded ? (
              <ExpandMore style={styles.toggleIcon} />
            ) : (
              <ChevronRight style={styles.toggleIcon} />
            )}
          </IconButton>
        ) : (
          <span style={styles.togglePlaceholder} />
        )}

        {getIcon()}

        <div style={{flexGrow: 1, overflow: "hidden"}}>
          <div style={styles.nameRow}>
            <div style={{flexGrow: 1}}>
              <Typography
                variant="body1"
                style={{
                  ...styles.nodeText,
                  ...(isCollection ? styles.collectionText : {}),
                  ...(isDocument && !isDocumentComplete
                    ? styles.documentTextProcessing
                    : {}),
                  ...(isDocumentComplete ? styles.documentTextComplete : {}),
                  ...(node.pending ? styles.pendingText : {}),
                  ...(isMsaRemoved
                    ? {textDecoration: "line-through", color: "#9e9e9e"}
                    : {}),
                }}
              >
                {node.name}
              </Typography>
              {node.type === "document" &&
                !hideDocumentMeta &&
                node.pending &&
                (!insideCombinedDocument || !combinedDocumentPending) && (
                  <Typography variant="caption" style={styles.documentMeta}>
                    <span style={styles.metaSpan}>To be uploaded</span>
                  </Typography>
                )}
              {node.type === "document" &&
                !hideDocumentMeta &&
                !node.pending &&
                renderUploaderMeta(node.data as MatterDocument)}
              {isCombinedDocument && !hideDocumentMeta && node.pending && (
                <Typography variant="caption" style={styles.documentMeta}>
                  <span style={styles.metaSpan}>To be uploaded</span>
                </Typography>
              )}
            </div>
            {(isStaleDocument ||
              canAddPursuantUpload ||
              isRemovableMsaFile) && (
              <div style={styles.documentActions}>
                {isStaleDocument && (
                  <Tooltip title="Analysed against a previous MSA">
                    <History style={styles.staleIcon} />
                  </Tooltip>
                )}
                {isRemovableMsaFile && (
                  <Tooltip
                    title={
                      isMsaRemoved
                        ? "Keep this MSA"
                        : canRemoveMsa
                        ? "Remove this MSA (replace)"
                        : "At least one MSA must remain"
                    }
                  >
                    <span>
                      <IconButton
                        size="small"
                        onClick={handleRemoveMsaFile}
                        disabled={!isMsaRemoved && !canRemoveMsa}
                        disableRipple={true}
                        aria-label={isMsaRemoved ? "keep MSA" : "remove MSA"}
                      >
                        {isMsaRemoved ? (
                          <Undo style={styles.addIcon} />
                        ) : (
                          <Close style={styles.addIcon} />
                        )}
                      </IconButton>
                    </span>
                  </Tooltip>
                )}
                {canAddPursuantUpload && (
                  <Tooltip title={addPursuantUploadTooltip}>
                    <IconButton
                      size="small"
                      onClick={handleAddPursuantUpload}
                      style={{
                        ...styles.addButton,
                        ...(isUploadTarget ? styles.addButtonActive : {}),
                      }}
                      disableRipple={true}
                    >
                      {isUploadTarget ? (
                        <RadioButtonChecked style={styles.addIcon} />
                      ) : (
                        <RadioButtonUnchecked style={styles.addIcon} />
                      )}
                    </IconButton>
                  </Tooltip>
                )}
              </div>
            )}
          </div>
          {showUploadStatus && (
            <div style={styles.uploadStatus}>
              <UploadStatus
                displayInDocDetail={displayInDocDetail}
                matterDocument={node.data as MatterDocument}
              />
            </div>
          )}
        </div>
      </Box>

      {isSOW && isViewingMSA && onChecklistDocumentSelect && (
        <div style={styles.checklistButtonContainer}>
          <Tooltip
            title={
              isChecklistDocument
                ? "Currently showing this document's checklist"
                : "Click to show this document's checklist"
            }
          >
            <span>
              <IconButton
                size="small"
                onClick={handleChecklistSelect}
                disabled={secondaryDocumentLoading}
                style={{
                  ...styles.checklistButton,
                  ...(isChecklistDocument || isHovered
                    ? styles.checklistButtonVisible
                    : styles.checklistButtonHidden),
                  ...(isChecklistDocument
                    ? styles.checklistButtonActive
                    : styles.checklistButtonInactive),
                }}
                disableRipple={true}
              >
                <CheckCircle
                  style={{
                    ...styles.checklistIcon,
                    ...(isChecklistDocument
                      ? styles.checklistIconActive
                      : styles.checklistIconInactive),
                  }}
                />
              </IconButton>
            </span>
          </Tooltip>
        </div>
      )}

      {hasChildren && (
        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
          <div style={styles.children}>
            {visibleChildren.map(child => (
              <MatterTreeNode
                key={`${child.type}-${child.id}`}
                node={child}
                level={level + 1}
                onNodeSelect={onNodeSelect}
                selectedNodeId={selectedNodeId}
                hideDocumentMeta={hideDocumentMeta}
                insideCombinedDocument={isCombinedDocument}
                combinedDocumentPending={
                  isCombinedDocument && Boolean(node.pending)
                }
                displayInDocDetail={displayInDocDetail}
                checklistDocumentId={checklistDocumentId}
                onChecklistDocumentSelect={onChecklistDocumentSelect}
                isViewingMSA={isViewingMSA}
                secondaryDocumentLoading={secondaryDocumentLoading}
                onAddPursuantUpload={onAddPursuantUpload}
                canUploadMsow={canUploadMsow}
                uploadTargetNodeId={uploadTargetNodeId}
                isAddingFiles={isAddingFiles}
                defaultCollapsedIds={defaultCollapsedIds}
                collapseKey={collapseKey}
                onRequestScroll={onRequestScroll}
                onRemoveMsaFile={onRemoveMsaFile}
                removedMsaIds={removedMsaIds}
                canRemoveMsa={canRemoveMsa}
                newestMsaCreationDate={newestMsaCreationDate}
              />
            ))}
            {collapseOtherSiblings && !showOtherSiblings && (
              <div
                style={{
                  ...styles.expandSiblingsRow,
                  paddingLeft: (level + 1) * 4 + 8,
                }}
                onClick={() => {
                  setShowOtherSiblings(true);
                  // Scroll the now-revealed siblings into view once expanded.
                  onRequestScroll?.(endMarkerRef.current);
                }}
                // Anchor the scroll here too, so targeting reveals the collapsed
                // siblings hint (just below the pending upload). See MatterTreeView.
                data-scroll-anchor="true"
              >
                <ChevronRight
                  style={{...styles.toggleIcon, color: "#1976d2"}}
                />
                <span>
                  Click to expand {otherSiblings.length} other {node.name}
                  {otherSiblings.length > 1 ? "s" : ""}…
                </span>
              </div>
            )}
          </div>
        </Collapse>
      )}

      {/* Bottom of this node's subtree; scrolled into view on expand/reveal. */}
      {hasChildren && <div ref={endMarkerRef} />}
    </div>
  );
};
